import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { AngularCsv } from "angular-csv-ext";
import { pipe } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { BulkUploadInterface } from "../../../../shared/types/bulk-upload/bulk-upload.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { AssetService } from "../asset.service";
import { AssetBulkUploadService } from "./asset-bulk-upload.service";
import { data } from "./asset-csv.json";

interface AssetBulkUploadPayload {
  csv: File;
}

interface CsvData {
  serialNumber: string;
  name: string;
  description: string;
  category: string;
  type: string;
  startDate: string;
  endDate: string;
  slaValue?: string;
  modelNumber?: string;
  oem?: string;
  contractNumber?: string;
  locationName?: string;
  companyIdentifier: string;
  partnerPrice?: number;
  clientPrice?: number;
  channelWorksCost?: number;
  monitoring?: boolean | string;
  dmr?: boolean | string;
  internalProvider?: string;
  hasConfigurationNotes?: boolean | string;
  decommissioned?: boolean | string;
  fairMarketValue?: number;
  bookValue?: number;
}

type CsvArray = CsvData[];

@Component({
  selector: "app-asset-bulk-upload",
  templateUrl: "./asset-bulk-upload.component.html",
  styleUrls: ["./asset-bulk-upload.component.css"],
  providers: [AssetBulkUploadService],
})
export class AssetBulkUploadComponent implements OnInit, BulkUploadInterface {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  questions: QuestionBase<string | boolean>[];
  currentUser: UserInfo;
  loading = false;
  channelWorksCostColumnName = "channelWorksCost";
  partnerPriceColumn = "partnerPrice";
  clientPriceColumnName = "clientPrice";
  internalProviderColumnName = "internalProviderName";
  hasConfigurationNotesColumnName = "hasConfigurationNotes";
  decommissionedColumnName = "decommissioned";
  fairMarketValueColumnName = "fairMarketValue";
  bookValueColumnName = "bookValue";

  constructor(
    private questionsService: AssetBulkUploadService,
    private assetsService: AssetService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    public location: Location,
    private router: Router,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  get isGlobal() {
    return this.currentUser.isGlobal();
  }

  ngOnInit(): void {
    this.questionsService.generateQuestions().subscribe((qs) => {
      this.questions = qs;
    });
  }

  onSubmit(values: AssetBulkUploadPayload): void {
    this.loading = true;

    this.assetsService
      .getPutSignedUrl()
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (r) => {
          this.assetsService
            .uploadBatch(r.url, values.csv)
            .pipe(finalize(() => this.dynamicForm.enable()))
            .subscribe(
              () => {
                this.notificationService.success(
                  "New bulk upload scheduled from csv file. You will be notified by email once the file is processed.",
                );
                this.loading = false;
              },
              () => {
                this.notificationService.error("Something went wrong. Try again later");
                this.loading = false;
              },
            );
        },
        () => {
          this.notificationService.error("Something went wrong. Try again later");
          this.loading = false;
        },
      );
  }

  goBack(): void {
    this.router.navigate(["/assets/create"]);
  }

  downloadCSV = (): void => {
    const json: CsvArray = data;
    const headers = [
      "serialNumber",
      "name",
      "description",
      "category",
      "type",
      "startDate",
      "endDate",
      "slaValue",
      "modelNumber",
      "oem",
      "contractNumber",
      "locationName",
      "companyIdentifier",
      "monitoring",
      "dmr",
    ];
    if (this.currentUser.isGlobal()) {
      headers.push(
        this.internalProviderColumnName,
        "channelWorksCost",
        "partnerPrice",
        this.hasConfigurationNotesColumnName,
        this.fairMarketValueColumnName,
        this.bookValueColumnName,
      );
      json.forEach((obj) => {
        obj.internalProvider = "An Internal Provider";
        obj.channelWorksCost = 100;
        obj.partnerPrice = 100;
        obj.hasConfigurationNotes = false;
        obj.fairMarketValue = 50;
        obj.bookValue = 50;
      });
    }
    if (this.currentUser.isGlobal() || this.currentUser.isPartner() || this.currentUser.isCompany()) {
      headers.push("clientPrice");
      json.forEach((obj) => {
        obj.clientPrice = 100;
      });
    }
    if (this.currentUser.isCompany()) {
      json.forEach((obj) => {
        obj.companyIdentifier = this.currentUser.company.companyIdentifier;
      });
    }
    headers.push(this.decommissionedColumnName);
    json.forEach((obj, index, array) => {
      obj.decommissioned = index === array.length - 1 ? "yes" : false;
    });
    const options = {
      useHeader: false,
      headers,
      nullToEmptyString: true,
      useBom: false,
    };
    new AngularCsv(json, "asset", options);
  };

  getFileExampleColumns() {
    const globalUserColumns = `${this.internalProviderColumnName},${this.channelWorksCostColumnName},${this.partnerPriceColumn},${this.clientPriceColumnName},${this.hasConfigurationNotesColumnName},${this.fairMarketValueColumnName},${this.bookValueColumnName}`;
    return `serialNumber,name,description,category,modelNumber,companyIdentifier,startDate,endDate,slaValue,type,oem,contractNumber,locationName,monitoring,dmr,${
      this.currentUser.isGlobal() ? globalUserColumns : this.clientPriceColumnName
    },decommissioned<br>`;
  }

  getFileExampleBody() {
    const globalUserData = "An Internal Provider,,,,,50,50";
    const assetCategoryExamples = ["subscription", "server", "workstations", "storage", "unassigned"];
    let fileBody = "";
    for (const [index, value] of assetCategoryExamples.entries()) {
      fileBody = fileBody.concat(
        `abc000${index},Asset${index},An asset,${value},Asset Model${index},CMPY0${index},2020/12/24,2021/01/16,123,,,,,,,${
          this.currentUser.isGlobal() ? globalUserData : ","
        }${index === assetCategoryExamples.length - 1 ? ",yes" : ""}<br>`,
      );
    }
    return fileBody;
  }
}
